import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import api from "../utils/api";
import { useAppState } from "./AppStateContext";

const ProposalOutputContext = createContext();

export default ProposalOutputContext;

export const ProposalOutputProvider = ({ children }) => {
  const navigate = useNavigate();

  const { apiRes, setApiRes } = useAppState();

  const sectionsArray = ['Summary', 'Objectives', 'Scope', 'Work Packages'];

  const [proposalId, setProposalId] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const scoreObj = {
    summary: '',
    objectives: '',
    scope: '',
    workPackages: '',
  };
  const [score, setScore] = useState(scoreObj);
  const [proposalSummary, setProposalSummary] = useState('');
  const [proposalObjectives, setProposalObjectives] = useState('');
  const [proposalScope, setProposalScope] = useState('');
  const [proposalWorkPackages, setProposalWorkPackages] = useState();
  const [sectionNameArray, setSectionNameArray] = useState([]);
  const [btnType, setBtnType] = useState('Draft');

  const validate = () => {
    const errors = {};

    for (const sectionName of sectionNameArray) {
      if (getScore(sectionName) === '') {
        errors[sectionName] = 'Score is required!';
      }
    }

    return errors;
  };

  useEffect(() => {
    if (sectionNameArray.length > 0) {
      setFormErrors(validate());
      setIsSubmit(true);
    }
  }, [sectionNameArray]);

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      for (const sectionName of sectionNameArray) {
        convertHtmlToSectionJSON(sectionName);
      }
      if (btnType === 'Sharepoint') {
        if (sectionNameArray.length === sectionsArray.length) {
          saveProposaltoSharepoint();
        }
      }
    }
  }, [formErrors]);

  useEffect(() => {
    if (proposalId !== '' && proposalId !== null) {
      getProposalDetails();
    }
  }, [proposalId]);

  const sectionHandlers = {
    Summary: {
      setContent: (data) => setProposalSummary(convertToHtml(data.content.summary)),
      scoreKey: "summary"
    },
    Objectives: {
      setContent: (data) => setProposalObjectives(convertToHtml(data.content, "Objectives")),
      scoreKey: "objectives"
    },
    Scope: {
      setContent: (data) => setProposalScope(convertToHtml(data.content)),
      scoreKey: "scope"
    },
    "Work Packages": {
      setContent: (data) => setProposalWorkPackages(convertWorkPackagesToHtml(data.content)),
      scoreKey: "workPackages"
    }
  };

  const getProposalDetails = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get('/api/proposal/details/get', {
        params: { proposal_uid: proposalId }
      });

      for (const data of response.data) {
        const handler = sectionHandlers[data.section_name];

        if (!handler) continue;

        handler.setContent(data);

        setScore(prev => ({
          ...prev,
          [handler.scoreKey]: (data.score === null) ? '' : data.score,
        }));
      }
      setApiRes({ ...apiRes, loading: false });
    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '')
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Proposal Details Get',
      });
    }
  };

  const convertWorkPackagesToHtml = (content) => {
    let html = '';

    const sorted = Object.keys(content)
      .sort()
      .reduce((acc, key) => {
        acc[key] = content[key];
        return acc;
      }, {});

    let index = 1;
    for (const key in sorted) {
      html += `<h3>Work Package No. ${index} - ${sorted[key]['title']}</h3>`;
      html += `<p>${sorted[key]['description']}</p>`
      html += `<br/>`;

      html += `<p><span style="font-weight: bold; margin-bottom: 0;">Tasks</span></p>`;
      html += `<ul ">`
      sorted[key]['tasks'].forEach(item => {
        html += `<li>${item}</li>`;
      });
      html += `</ul>`;
      html += `<br/>`;

      html += `<p><span style="font-weight: bold;">Deliverables</span></p>`;
      html += `<ol>`
      sorted[key]['deliverables'].forEach(item => {
        html += `<li>${item}</li>`;
      });
      html += `</ol>`;
      html += `<br/>`;

      html += `<p><span style="font-weight: bold;">Assumptions</span></p>`;
      html += `<ol >`
      sorted[key]['assumptions'].forEach(item => {
        html += `<li>${item}</li>`;
      });
      html += `</ol>`;
      html += `<br/>`;

      html += `<strong>Estimated Delivery Schedule:</strong> ${sorted[key]['estimated_delivery_schedule_months']} Months`;
      html += `<br/>`;
      html += `<strong>Price (ex. VAT or other applicable taxes):</strong> £${sorted[key]['price']}`;
      html += `<br/>`;
      html += `<br/>`;

      index++;
    }

    return html;
  };

  const convertToHtml = (content, title = null) => {
    let html = '';

    if (title === 'Objectives') {
      const sorted = Object.keys(content)
        .sort()
        .reduce((acc, key) => {
          acc[key] = content[key];
          return acc;
        }, {});
      content = sorted;
    }

    if (typeof content === 'string') {
      html += `<p>${content}</p>`;
    } else {
      for (const key in content) {
        const value = content[key];

        if (Array.isArray(value)) {
          html += `<ul>`;
          value.forEach(item => {
            html += `<li>${item}</li>`;
          });
          html += `</ul>`;
        } else if (typeof value === 'object' && value !== null) {
          html += `<div>${convertToHtml(value)}</div>`;
        } else {
          html += `<p><strong>${key}:</strong> ${value}</p>`;
          html += `<br/>`;
        }
      }
    }
    return html;
  };

  const handleEditorChange = (content, title) => {
    if (title === 'Summary') {
      setProposalSummary(content);
    } else if (title === 'Objectives') {
      setProposalObjectives(content);
    } else if (title === 'Scope') {
      setProposalScope(content);
    } else if (title === 'Work Packages') {
      setProposalWorkPackages(content);
    }
  };

  const handleSaveProposaltoSharepointBtnClick = () => {
    setBtnType('Sharepoint');
    setSectionNameArray(sectionsArray);
  };

  const saveProposaltoSharepoint = async () => {
    setApiRes({ ...apiRes, loading: true });

    try {
      const response = await api.post('/api/proposal/save/to/sharepoint', {
        proposal_uid: proposalId,
      });

      setApiRes({
        ...apiRes,
        showAlert: true,
        successMsg: response.data,
        loading: false,
      });
    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Save proposal to SharePoint',
      });
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const getScore = (title) => {
    if (title === 'Summary') {
      return score?.summary;
    } else if (title === 'Objectives') {
      return score?.objectives;
    } else if (title === 'Scope') {
      return score?.scope;
    } else if (title === 'Work Packages') {
      return score?.workPackages;
    } else {
      return 1;
    }
  };

  const handleScoreChange = (title, value) => {
    let key = '';
    if (title === 'Summary') {
      key = 'summary';
    } else if (title === 'Objectives') {
      key = 'objectives';
    } else if (title === 'Scope') {
      key = 'scope';
    } else if (title === 'Work Packages') {
      key = 'workPackages';
    }

    if (key !== '') {
      setScore(prev => ({
        ...prev,
        [key]: +value
      }));
    }
  };

  const getProposalContent = (title) => {
    if (title === 'Summary') {
      return proposalSummary;
    } else if (title === 'Objectives') {
      return proposalObjectives;
    } else if (title === 'Scope') {
      return proposalScope;
    } else if (title === 'Work Packages') {
      return proposalWorkPackages;
    }
  };

  const convertHtmlToSummary = () => {
    const htmlString = proposalSummary;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const paragraphs = Array.from(doc.querySelectorAll("p"))
      .map(p => p.textContent.trim())
      .filter(text => text.length > 0);

    return { 'summary': paragraphs.join("\n\n") };
  };

  const convertHtmlToObjectives = () => {
    const htmlString = proposalObjectives;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const result = {};

    doc.querySelectorAll("p").forEach(p => {
      const strong = p.querySelector("strong");
      if (strong) {
        const key = strong.textContent.replace(/:$/, "").trim();
        const fullText = p.textContent.trim();
        const value = fullText.replace(strong.textContent, "").trim();

        if (value) result[key] = value;
      }
    });

    return result;
  };

  const convertHtmlToScope = () => {
    const htmlString = proposalScope;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const paragraphs = Array.from(doc.querySelectorAll("p"))
      .map(p => p.textContent.trim())
      .filter(text => text.length > 0);

    return paragraphs.join("\n\n");
  };

  const convertHtmlToWorkPackages = () => {
    const htmlString = proposalWorkPackages;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const workPackages = {};
    const h3s = doc.querySelectorAll("h3");

    h3s.forEach((h3, index) => {
      const titleText = h3.textContent.trim();
      const wpMatch = titleText.match(/Work Package No\. (\d+)\s*-\s*(.*)/);
      if (!wpMatch) return;

      const wpNo = `WP${wpMatch[1]}`;
      const wpTitle = wpMatch[2].trim();

      const wp = {
        title: wpTitle,
        description: "",
        tasks: [],
        deliverables: [],
        assumptions: [],
        estimated_delivery_schedule_months: null,
        price: null
      };

      const elements = [];
      let el = h3.nextElementSibling;
      while (el && el.tagName !== "H3") {
        elements.push(el);
        el = el.nextElementSibling;
      }

      const firstP = elements.find(e => e.tagName === "P" && e.textContent.trim());
      if (firstP) wp.description = firstP.textContent.trim();

      let currentSection = null;
      elements.forEach(e => {
        if (e.tagName === "P" && e.querySelector("strong")) {
          const sectionTitle = e.textContent.trim().replace(/:$/, "");
          if (["Tasks", "Deliverables", "Assumptions"].includes(sectionTitle)) {
            currentSection = sectionTitle.toLowerCase();
          } else if (sectionTitle.startsWith("Estimated Delivery Schedule")) {
            const match = e.textContent.match(/(\d+)\s*Month/i);
            if (match) wp.estimated_delivery_schedule_months = parseInt(match[1]);
          } else if (sectionTitle.startsWith("Price")) {
            const match = e.textContent.match(/£?([\d,]+)/);
            if (match) wp.price = parseInt(match[1].replace(/,/g, ""));
          }
        }

        if ((e.tagName === "UL" || e.tagName === "OL") && currentSection) {
          const items = Array.from(e.querySelectorAll("li")).map(li =>
            li.textContent.trim()
          );
          wp[currentSection] = [...wp[currentSection], ...items];
        }
      });

      workPackages[wpNo] = wp;
    });

    return workPackages;
  };

  const convertHtmlToSectionJSON = (sectionName) => {
    let sectionData = {};

    if (sectionName === 'Summary') {
      sectionData = convertHtmlToSummary();
    } else if (sectionName === 'Objectives') {
      sectionData = convertHtmlToObjectives();
    } else if (sectionName === 'Scope') {
      sectionData = convertHtmlToScope();
    } else if (sectionName === 'Work Packages') {
      sectionData = convertHtmlToWorkPackages();
    }

    proposalSectionSave(sectionName, sectionData);
  };

  const proposalSectionSave = async (sectionName, sectionData) => {
    setApiRes({ ...apiRes, loading: true });

    try {
      const formData = new FormData();
      formData.append("proposal_id", proposalId);
      formData.append("section_name", sectionName);
      formData.append("content", JSON.stringify(sectionData));
      formData.append("score", getScore(sectionName));

      const response = await api.post('/api/proposal/section/save', formData);

      setApiRes({
        ...apiRes,
        showAlert: true,
        successMsg: response.data,
        loading: false,
      });
    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Proposal Section Save',
      });
    }
  };

  const handleSaveDraftBtnClick = () => {
    setBtnType('Draft');
    setSectionNameArray(sectionsArray);
  };

  const contextData = {
    formErrors: formErrors,
    setSectionNameArray: setSectionNameArray,
    handleEditorChange: handleEditorChange,
    handleSaveProposaltoSharepointBtnClick: handleSaveProposaltoSharepointBtnClick,
    handleSaveDraftBtnClick: handleSaveDraftBtnClick,
    handleBack: handleBack,
    sectionsArray: sectionsArray,
    getScore: getScore,
    handleScoreChange: handleScoreChange,
    getProposalContent: getProposalContent,
    setProposalId: setProposalId,
  };

  return (
    <ProposalOutputContext.Provider value={contextData}>
      {children}
    </ProposalOutputContext.Provider>
  )
};